/**
 * PLANNING A DAY, WITHOUT PRETENDING TO HAVE ROUTED IT.
 *
 * ROUTE-01, and docs/ROUTE-PLANNER.md holds the owner's own words for it. He
 * asked for a route built automatically from the addresses, the hours and a
 * start and end point. Nothing in this product turns an address into a
 * position, so the automatic part of that is a geocoding service, an account,
 * a per request cost, and customer addresses leaving this app. That is a
 * decision somebody makes, not a task somebody does.
 *
 * SO THIS IS THE HALF THAT IS TRUE TODAY, and it is most of the value. The
 * contractor picks the order, because he knows his town; the app does the
 * arithmetic, which is the part people get wrong at six in the morning. It
 * lays the stops end to end from a start time, adds the travel allowance he
 * types, and says what time the day actually finishes and by how much it
 * overruns the hour he wanted to be done.
 *
 * THE TRAVEL ALLOWANCE IS THE HONEST BRIDGE. A number he types is local
 * knowledge this product does not have and cannot invent, and it makes the
 * finish time real without a single address ever leaving the device. What it
 * is not is a measured drive time, and nothing here calls it one.
 *
 * SCHED-01 says the same in its own words: unknown travel displays
 * unavailable, and optimization explains unassigned stops.
 */

export interface Stop {
  jobId: string
  label: string
  /** How long the work takes, in minutes. His estimate, not a calculation. */
  minutes: number
}

export interface Laid extends Stop {
  /** Minutes past midnight, wall clock at the house. */
  from: number
  to: number
  /** The allowance added before this stop. Zero on the first one. */
  travelBefore: number
}

export interface Plan {
  /** Minutes past midnight the day starts. */
  startMin: number
  /** The hour he wants to be finished by. */
  endMin: number
  /** Minutes he allows between one house and the next. Typed, never measured. */
  travelMin: number
  stops: Stop[]
}

/**
 * END TO END FROM THE START TIME, with the allowance between them.
 *
 * No allowance before the first stop: he is already at the first house, or the
 * drive to it happened before the day started. Adding one there is how a
 * planner quietly loses half an hour every single morning.
 */
export function layOut(p: Plan): Laid[] {
  const out: Laid[] = []
  let at = p.startMin
  const gap = Math.max(0, Math.round(p.travelMin))
  for (let i = 0; i < p.stops.length; i++) {
    const s = p.stops[i]
    const travel = i === 0 ? 0 : gap
    at += travel
    const mins = Math.max(0, Math.round(s.minutes))
    out.push({ ...s, from: at, to: at + mins, travelBefore: travel })
    at += mins
  }
  return out
}

export const finishOf = (rows: Laid[], startMin: number): number =>
  rows.length === 0 ? startMin : rows[rows.length - 1].to

/** Work, and travel, kept apart, because only one of them is a guess. */
export function totals(p: Plan): { work: number; travel: number; whole: number } {
  const work = p.stops.reduce((n, s) => n + Math.max(0, Math.round(s.minutes)), 0)
  const travel = Math.max(0, p.stops.length - 1) * Math.max(0, Math.round(p.travelMin))
  return { work, travel, whole: work + travel }
}

/** How far past the hour he wanted to be done. Zero or less means it fits. */
export function overrun(p: Plan): number {
  const rows = layOut(p)
  return finishOf(rows, p.startMin) - p.endMin
}

/**
 * WHICH STOPS DO NOT FIT.
 *
 * SCHED-01 asks that optimization explain unassigned stops. There is no
 * optimization here, so the explanation is simpler and better: these are the
 * ones that fall past the hour you wanted to finish, in the order you put
 * them, and moving one up is how that changes.
 */
export function pastTheEnd(p: Plan): string[] {
  return layOut(p)
    .filter((r) => r.to > p.endMin)
    .map((r) => r.jobId)
}

/** Move a stop one place, which is the only reordering a person needs. */
export function move(stops: Stop[], jobId: string, by: -1 | 1): Stop[] {
  const i = stops.findIndex((s) => s.jobId === jobId)
  if (i < 0) return stops
  const to = i + by
  if (to < 0 || to >= stops.length) return stops
  const next = [...stops]
  const [x] = next.splice(i, 1)
  next.splice(to, 0, x)
  return next
}

export const NOT_ROUTED: string[] = [
  'The order is yours, not the shortest one. Nothing here turns an address into a position, so nothing here can put four houses in the best order.',
  'The travel allowance is a number you typed. It is local knowledge this product does not have, and it is not a measured drive time.',
  'Where the day ends. Finishing at the warehouse or at home is another leg, and it is not counted because there is no distance behind it either.',
]
